
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Lab Orders Service
 * Uses backend API endpoints instead of direct Firestore access
 */

// Add a new order
export async function addLabOrder(data) {
  try {
    console.log('Adding lab order via API:', data.patientId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/clinical/lab-orders`,
      data,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      id: response.data.id,
      ...response.data
    };
  } catch (error) {
    console.error('Error adding lab order:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// List orders (optionally by patient)
export async function getLabOrders({ patientId, facilityId, status, page = 1, limit = 50 } = {}) {
  try {
    console.log('Getting lab orders via API:', { patientId, facilityId, status });
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    if (patientId) params.append('patientId', patientId);
    if (facilityId) params.append('facilityId', facilityId);
    if (status) params.append('status', status);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    const response = await axios.get(
      `${API_BASE}/clinical/lab-orders?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.labOrders.map(order => ({ 
      id: order.id, 
      ...order 
    }));
  } catch (error) {
    console.error('Error getting lab orders:', error);
    throw error;
  }
}

// Get a specific lab order by ID
export async function getLabOrder(orderId) {
  try {
    console.log('Getting lab order by ID via API:', orderId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/lab-orders/${orderId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      id: response.data.labOrder.id,
      ...response.data.labOrder
    };
  } catch (error) {
    console.error('Error getting lab order:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// Update order status
export async function updateLabOrderStatus(id, newStatus, userId) {
  try {
    console.log('Updating lab order status via API:', id, newStatus);
    const token = await getAuthToken();
    
    await axios.put(
      `${API_BASE}/clinical/lab-orders/${id}/status`,
      { status: newStatus },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return { success: true };
  } catch (error) {
    console.error('Error updating lab order status:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// Delete order
export async function deleteLabOrder(id) {
  try {
    console.log('Deleting lab order via API:', id);
    const token = await getAuthToken();
    
    await axios.delete(
      `${API_BASE}/clinical/lab-orders/${id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting lab order:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// Additional convenience functions

/**
 * Get lab orders for a specific patient
 */
export async function getPatientLabOrders(patientId, options = {}) {
  try {
    return await getLabOrders({ 
      patientId, 
      ...options 
    });
  } catch (error) {
    console.error('Error getting patient lab orders:', error);
    throw error;
  }
}

/**
 * Get lab orders by status
 */
export async function getLabOrdersByStatus(status, facilityId = null, page = 1, limit = 50) {
  try {
    const filters = { status, page, limit };
    if (facilityId) {
      filters.facilityId = facilityId;
    }
    return await getLabOrders(filters);
  } catch (error) {
    console.error('Error getting lab orders by status:', error);
    throw error;
  }
}

/**
 * Get pending lab orders
 */
export async function getPendingLabOrders(facilityId = null) {
  try {
    return await getLabOrdersByStatus('ordered', facilityId);
  } catch (error) {
    console.error('Error getting pending lab orders:', error);
    throw error;
  }
}

/**
 * Get completed lab orders
 */
export async function getCompletedLabOrders(facilityId = null, page = 1, limit = 50) {
  try {
    return await getLabOrdersByStatus('completed', facilityId, page, limit);
  } catch (error) {
    console.error('Error getting completed lab orders:', error);
    throw error;
  }
}

/**
 * Cancel a lab order
 */
export async function cancelLabOrder(orderId, userId, reason = '') {
  try {
    console.log('Cancelling lab order via API:', orderId);
    const token = await getAuthToken();
    
    await axios.put(
      `${API_BASE}/clinical/lab-orders/${orderId}/status`,
      { 
        status: 'cancelled',
        cancellationReason: reason
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return { success: true };
  } catch (error) {
    console.error('Error cancelling lab order:', error);
    throw error;
  }
}

/**
 * Search lab orders
 */
export async function searchLabOrders(searchTerm, filters = {}) {
  try {
    console.log('Searching lab orders via API:', searchTerm);
    
    // For now, we'll do a basic search by getting all orders and filtering
    // In a production system, the backend would handle the search logic
    const orders = await getLabOrders({
      ...filters,
      limit: 1000 // Get more results for search
    });
    
    if (!searchTerm || searchTerm.trim() === '') {
      return orders;
    }
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return orders.filter(order => 
      (order.testName && order.testName.toLowerCase().includes(lowerSearchTerm)) ||
      (order.patientName && order.patientName.toLowerCase().includes(lowerSearchTerm)) ||
      (order.orderNumber && order.orderNumber.toLowerCase().includes(lowerSearchTerm)) ||
      (order.notes && order.notes.toLowerCase().includes(lowerSearchTerm))
    );
  } catch (error) {
    console.error('Error searching lab orders:', error);
    throw error;
  }
}

/**
 * Get lab order statistics
 */
export async function getLabOrderStats(facilityId = null, dateRange = {}) {
  try {
    console.log('Getting lab order statistics via API:', facilityId);
    
    // Get all orders for the facility
    const filters = { limit: 1000 };
    if (facilityId) {
      filters.facilityId = facilityId;
    }
    
    const orders = await getLabOrders(filters);
    
    // Filter by date range if provided
    let filteredOrders = orders;
    if (dateRange.startDate || dateRange.endDate) {
      filteredOrders = orders.filter(order => {
        const orderDate = new Date(order.createdAt);
        if (dateRange.startDate && orderDate < new Date(dateRange.startDate)) {
          return false;
        }
        if (dateRange.endDate && orderDate > new Date(dateRange.endDate)) {
          return false;
        }
        return true;
      });
    }
    
    const stats = {
      total: filteredOrders.length,
      ordered: 0,
      inProgress: 0,
      completed: 0,
      cancelled: 0,
      byTestType: {},
      byDay: {}
    };
    
    filteredOrders.forEach(order => {
      // Count by status
      switch (order.status) {
        case 'ordered':
          stats.ordered++;
          break;
        case 'in_progress':
        case 'processing':
          stats.inProgress++;
          break;
        case 'completed':
          stats.completed++;
          break;
        case 'cancelled':
          stats.cancelled++;
          break;
      }
      
      // Count by test type
      const testType = order.testType || 'Unknown';
      stats.byTestType[testType] = (stats.byTestType[testType] || 0) + 1;
      
      // Count by day
      const day = new Date(order.createdAt).toISOString().split('T')[0];
      stats.byDay[day] = (stats.byDay[day] || 0) + 1;
    });
    
    return stats;
  } catch (error) {
    console.error('Error getting lab order statistics:', error);
    throw error;
  }
}

/**
 * Bulk update lab order statuses
 */
export async function bulkUpdateLabOrderStatus(orderIds, newStatus, userId) {
  try {
    console.log('Bulk updating lab order statuses via API:', orderIds.length, newStatus);
    
    const promises = orderIds.map(orderId => 
      updateLabOrderStatus(orderId, newStatus, userId)
    );
    
    const results = await Promise.allSettled(promises);
    
    const summary = {
      successful: 0,
      failed: 0,
      errors: []
    };
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        summary.successful++;
      } else {
        summary.failed++;
        summary.errors.push({
          orderId: orderIds[index],
          error: result.reason
        });
      }
    });
    
    return summary;
  } catch (error) {
    console.error('Error bulk updating lab order statuses:', error);
    throw error;
  }
}

/**
 * Get lab order history for a patient
 */
export async function getPatientLabOrderHistory(patientId, limit = 100) {
  try {
    console.log('Getting patient lab order history via API:', patientId);
    
    const orders = await getPatientLabOrders(patientId, { 
      limit,
      // The backend orders by createdAt desc by default
    });
    
    // Group by date for better visualization
    const history = {};
    orders.forEach(order => {
      const date = new Date(order.createdAt).toISOString().split('T')[0];
      if (!history[date]) {
        history[date] = [];
      }
      history[date].push(order);
    });
    
    return history;
  } catch (error) {
    console.error('Error getting patient lab order history:', error);
    throw error;
  }
}

/**
 * Get urgent lab orders
 */
export async function getUrgentLabOrders(facilityId = null) {
  try {
    console.log('Getting urgent lab orders via API:', facilityId);
    
    const filters = { limit: 1000 };
    if (facilityId) {
      filters.facilityId = facilityId;
    }
    
    const orders = await getLabOrders(filters);
    
    // Filter for urgent orders
    return orders.filter(order => 
      order.priority === 'urgent' || 
      order.priority === 'stat' ||
      order.urgent === true
    );
  } catch (error) {
    console.error('Error getting urgent lab orders:', error);
    throw error;
  }
}

/**
 * Export lab orders to CSV-like format
 */
export async function exportLabOrders(filters = {}, format = 'json') {
  try {
    console.log('Exporting lab orders via API:', filters, format);
    
    const orders = await getLabOrders({
      ...filters,
      limit: 10000 // Get all orders for export
    });
    
    if (format === 'json') {
      return JSON.stringify(orders, null, 2);
    } else if (format === 'csv') {
      // Simple CSV conversion
      const headers = ['ID', 'Patient', 'Test Name', 'Status', 'Ordered Date', 'Updated Date'];
      const rows = orders.map(order => [
        order.id,
        order.patientName || 'Unknown',
        order.testName || 'Unknown',
        order.status,
        new Date(order.createdAt).toLocaleDateString(),
        new Date(order.updatedAt).toLocaleDateString()
      ]);
      
      return [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    }
    
    throw new Error(`Unsupported export format: ${format}`);
  } catch (error) {
    console.error('Error exporting lab orders:', error);
    throw error;
  }
}
